import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Checkbox, FormControlLabel, Paper, Alert, CircularProgress, useMediaQuery, useTheme, IconButton, Stack, Tooltip, Chip,
  Grid, Card, CardContent, Avatar
} from '@mui/material';
import { Add, Edit, Delete, ToggleOn, ToggleOff, Phone, Work, Person } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { maintenanceStaffAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const EmployeeDetails = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch staff list — normalize to an array to avoid cached axios response object
  const { data: staff = [], isLoading, isError } = useQuery(['maintenance-staff', 'list'], async () => {
    const res = await maintenanceStaffAPI.getAll();
    return Array.isArray(res.data) ? res.data : (res.data?.items || []);
  });

  // Dialog state
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
  const [errorMsg, setErrorMsg] = useState('');

  const createMutation = useMutation((newStaff) => maintenanceStaffAPI.create(newStaff), {
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-staff','list']);
      setOpen(false);
      setForm({ userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
      setErrorMsg('');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err.message || 'Failed to add staff';
      setErrorMsg(msg);
    }
  });

  const updateMutation = useMutation(({ id, data }) => maintenanceStaffAPI.update(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-staff','list']);
      setOpen(false);
      setForm({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
      setErrorMsg('');
      setIsEditing(false);
      setEditingId(null);
    },
    onError: (err) => setErrorMsg(err?.response?.data?.message || err.message || 'Failed to update staff')
  });

  const deleteMutation = useMutation((id) => maintenanceStaffAPI.delete(id), {
    onSuccess: () => queryClient.invalidateQueries('maintenance-staff'),
    onError: (err) => setErrorMsg(err?.response?.data?.message || err.message || 'Failed to delete staff')
  });

  const [isEditing, setIsEditing] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);

  const handleOpen = (e) => {
    // Ensure the element that opened the dialog does not retain focus
    // because MUI sets `aria-hidden` on the app root when mounting the dialog.
    // If a focused descendant remains, the browser will block the aria-hidden change.
    try {
      if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
        e.currentTarget.blur();
      } else if (document && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (err) {
      // swallow any errors — this is a best-effort accessibility fix
    }
    setOpen(true);
    setErrorMsg('');
    setIsEditing(false);
    setEditingId(null);
    setForm({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
  };
  const handleOpenForEdit = (member) => {
    setForm({
      name: member.name || '',
      userId: member.userId || '',
      phoneNumber: member.phoneNumber || '',
      specialization: member.specialization || '',
      isAvailable: !!member.isAvailable,
      rating: member.rating || ''
    });
    setIsEditing(true);
    setEditingId(member.staffId || member.userId);
    setOpen(true);
    setErrorMsg('');
  };

  // Auto-open edit dialog when ?id= is present in URL
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    if (!id) return;

    // If staff list already contains the member, open immediately
    const found = (staff || []).find(s => (s.staffId === id) || (s.userId === id));
    if (found) {
      handleOpenForEdit(found);
      // remove query param
      params.delete('id');
      navigate({ search: params.toString() }, { replace: true });
      return;
    }

    // Otherwise fetch by id and open
    (async () => {
      try {
        const res = await maintenanceStaffAPI.getById(id);
        const member = Array.isArray(res.data) ? res.data[0] : res.data;
        if (member) handleOpenForEdit(member);
      } catch (err) {
        console.error('Failed to fetch staff by id from query param', err);
      } finally {
        params.delete('id');
        navigate({ search: params.toString() }, { replace: true });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, staff]);
  const handleClose = () => { setOpen(false); setErrorMsg(''); };

  // Group staff by specialization for headered list
  const groupedBySpecialization = React.useMemo(() => {
    const map = {};
    (staff || []).forEach(s => {
      const key = s.specialization && s.specialization.trim() ? s.specialization.trim() : 'Header';
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [staff]);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // (userId is assigned by backend on create; frontend won't send userId when adding)

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    // Basic validation: phoneNumber always required; userId required only when editing
    if (!form.phoneNumber) {
      setErrorMsg('Phone Number is required');
      return;
    }
    if (isEditing && !form.userId) {
      setErrorMsg('User ID is required for editing');
      return;
    }

    // Prepare payload - do not send userId on create so backend can assign it
    const payload = {
      name: form.name,
      phoneNumber: form.phoneNumber,
      specialization: form.specialization,
      isAvailable: !!form.isAvailable,
      rating: form.rating ? Number(form.rating) : undefined
    };

    if (isEditing && editingId) {
      // include userId for updates
      payload.userId = form.userId;
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) return;
    deleteMutation.mutate(id);
  };

  const handleToggleActive = (member) => {
    const id = member.staffId || member.userId;
    if (!id) return;
    updateMutation.mutate({ id, data: { isActive: !member.isActive } });
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'}>{t('layout.navigation.EmployeeDetails') || 'Employee Details'}</Typography>
        {!readOnly && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpen} size={isMobile ? 'small' : 'medium'}>
            {t('common.add') || 'Add Staff'}
          </Button>
        )}
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : isError ? (
        <Alert severity="error">Failed to load staff</Alert>
      ) : (
        <Paper>
          <Box sx={{ p: 2 }}>
            {Object.keys(groupedBySpecialization).map((spec) => (
              <Box key={spec} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>{spec}</Typography>
                <Grid container spacing={2}>
                  {groupedBySpecialization[spec].map((m) => (
                    <Grid item xs={12} sm={6} md={4} key={m.userId || m.staffId}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar>
                                <Person />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{m.name || '-'}</Typography>
                                <Typography variant="caption" color="text.secondary">{m.userId || '-'}</Typography>
                              </Box>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              {!readOnly && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleOpenForEdit(m)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={m.isActive ? 'Disable' : 'Enable'}>
                                    <IconButton size="small" onClick={() => handleToggleActive(m)}>
                                      {m.isActive ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDelete(m.staffId || m.userId)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                          </Box>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone fontSize="small" color="action" />
                              <Typography variant="body2">{m.phoneNumber || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Work fontSize="small" color="action" />
                              <Typography variant="body2">{m.specialization || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={m.isActive ? 'Active' : 'Disabled'} color={m.isActive ? 'success' : 'default'} size="small" />
                              <Typography variant="caption" color="text.secondary">{m.isAvailable ? 'Available' : 'Not Available'}</Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{(isEditing ? (t('common.edit') || 'Modify') : (t('common.add') || 'Add')) + ' ' + (t('layout.navigation.EmployeeDetails') || 'Staff')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
            <TextField fullWidth label="Name" value={form.name} onChange={handleChange('name')} sx={{ mb: 2 }} />
            <TextField fullWidth label="User ID" value={form.userId} onChange={handleChange('userId')} sx={{ mb: 2 }} required />
            <TextField fullWidth label="Phone Number" value={form.phoneNumber} onChange={handleChange('phoneNumber')} sx={{ mb: 2 }} required />
            <TextField fullWidth label="Specialization" value={form.specialization} onChange={handleChange('specialization')} sx={{ mb: 2 }} />
            <TextField fullWidth label="Rating" value={form.rating} onChange={handleChange('rating')} sx={{ mb: 2 }} type="number" inputProps={{ min: 0, max: 5 }} />
            <FormControlLabel control={<Checkbox checked={form.isAvailable} onChange={handleChange('isAvailable')} />} label="Available" />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isEditing ? updateMutation.isLoading : createMutation.isLoading}>
              {isEditing ? (updateMutation.isLoading ? 'Updating...' : 'Update') : (createMutation.isLoading ? 'Adding...' : 'Add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EmployeeDetails;
